import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  CircularProgress,
  Chip,
  Stack,
  Divider,
  Avatar,
  IconButton,
  LinearProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../store';
import { sectorCanCreateEvents } from '../../permissions';
import { AUDIENCE_FUNNEL_UI_ENABLED } from '../../features/audience-funnel/featureFlag';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';
import { dashboardService, registrationService } from '../../services/dashboardService';
import { getCurrentAuthContext } from '../../services/auth';
import { eventService } from '../../services/eventService';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import GroupIcon from '@mui/icons-material/Group';
import PaymentsIcon from '@mui/icons-material/Payments';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';

const DashboardPage: React.FC = () => {
  const t = useBrandRegistrationCopy();
  const { user } = useSelector((state: RootState) => state.auth);
  const canCreateEvents = sectorCanCreateEvents(user);
  const authContext = getCurrentAuthContext();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalEvents: 0,
    activeEvents: 0,
    totalRegistrations: 0,
    totalRevenue: 0,
  });

  const [loading, setLoading] = useState(true);
  const [highlightedEvents, setHighlightedEvents] = useState<
    Array<{
      id: string;
      title: string;
      coverImage?: string;
      locationLabel: string;
      startDate: Date;
      status: string;
    }>
  >([]);
  const [recentRegistrations, setRecentRegistrations] = useState<
    Array<{ id: string; participantName: string; eventTitle: string; status: string }>
  >([]);
  const [registrationMetrics, setRegistrationMetrics] = useState({
    confirmed: 0,
    pending: 0,
  });

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value || 0);

  const formatEventStatus = (status: string) => {
    const normalized = status?.toLowerCase();
    if (normalized === 'published') return { label: 'INSCRIÇÕES ABERTAS', color: 'success' as const };
    if (normalized === 'paused') return { label: 'PAUSADO', color: 'warning' as const };
    if (normalized === 'pre_published') return { label: 'AGUARDANDO APROVAÇÃO', color: 'info' as const };
    if (normalized === 'test') return { label: 'EM TESTE', color: 'default' as const };
    return { label: 'FINALIZADO', color: 'default' as const };
  };

  useEffect(() => {
    if (authContext === 'parish') {
      // No contexto de paróquia, sempre tentar carregar dados do dashboard
      Promise.all([
        dashboardService.getParishDashboardSummary(),
        eventService.getEvents(user?.parishId ? { parishId: user.parishId } : undefined),
        registrationService.getAll(),
      ])
        .then(([summary, eventsResponse, registrations]) => {
          setStats({
            totalEvents: Number(summary?.totalEvents) || 0,
            activeEvents: Number(summary?.activeEvents) || 0,
            totalRegistrations: Number(summary?.totalRegistrations) || 0,
            totalRevenue: Number(summary?.totalRevenue) || 0,
          });
          if (eventsResponse.success && eventsResponse.data) {
            const formatted = eventsResponse.data
              .slice()
              .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime())
              .slice(0, 3)
              .map((event) => {
                const cityState = [event.city, event.state].filter(Boolean).join(', ');
                return {
                  id: event._id,
                  title: event.title,
                  coverImage: event.coverImage,
                  locationLabel: cityState || event.location || 'Local a definir',
                  startDate: new Date(event.startDate),
                  status: event.status,
                };
              });
            setHighlightedEvents(formatted);
          }
          setRecentRegistrations(
            (registrations || [])
              .slice()
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .slice(0, 4)
              .map((reg) => ({
                id: reg.id,
                participantName: reg.participantName || 'Participante',
                eventTitle: reg.eventTitle || 'Evento',
                status: reg.status || 'pending',
              }))
          );
          const confirmed = (registrations || []).filter((reg) =>
            ['confirmed', 'attended'].includes((reg.status || '').toLowerCase())
          ).length;
          const pending = (registrations || []).filter((reg) =>
            ['pending'].includes((reg.status || '').toLowerCase()) ||
            ['pending'].includes((reg.paymentStatus || '').toLowerCase())
          ).length;
          setRegistrationMetrics({ confirmed, pending });
        })
        .catch(err => {
          console.error('Erro ao carregar dashboard:', err);
          // Se der erro, definir dados básicos
          setStats({
            totalEvents: 0,
            activeEvents: 0,
            totalRegistrations: 0,
            totalRevenue: 0,
          });
          setHighlightedEvents([]);
          setRecentRegistrations([]);
          setRegistrationMetrics({ confirmed: 0, pending: 0 });
        })
        .finally(() => setLoading(false));
    } else if (authContext === 'admin') {
      // No contexto admin, mostrar interface de super admin
      setStats({
        totalEvents: 0,
        activeEvents: 0,
        totalRegistrations: 0,
        totalRevenue: 0,
      });
      setLoading(false);
    } else {
      setLoading(false);
    }
  }, [user, authContext, user?.parishId]);

  const safeStats = {
    totalEvents: Number(stats?.totalEvents) || 0,
    activeEvents: Number(stats?.activeEvents) || 0,
    totalRegistrations: Number(stats?.totalRegistrations) || 0,
    totalRevenue: Number(stats?.totalRevenue) || 0,
  };

  const statsCards = [
    {
      title: 'Total de Eventos',
      value: String(safeStats.totalEvents),
      helper: 'Eventos criados na plataforma',
      icon: <CalendarMonthIcon fontSize="small" />,
      color: 'primary.main',
    },
    {
      title: 'Eventos Ativos',
      value: String(safeStats.activeEvents),
      helper: t('Eventos com inscrições abertas'),
      icon: <EventAvailableIcon fontSize="small" />,
      color: 'success.main',
    },
    {
      title: t('Total de Inscrições'),
      value: String(safeStats.totalRegistrations),
      helper: 'Participações registradas',
      icon: <GroupIcon fontSize="small" />,
      color: 'info.main',
    },
    {
      title: 'Receita Total',
      value: formatCurrency(safeStats.totalRevenue),
      helper: 'Volume total arrecadado',
      icon: <PaymentsIcon fontSize="small" />,
      color: 'warning.main',
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: { xs: 12, md: 5 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.75, md: 2.25 },
          mb: { xs: 2, md: 3 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography
              component="h1"
              sx={{ fontWeight: 700, fontSize: { xs: '2rem', md: '2.125rem' }, lineHeight: 1.1 }}
            >
              {t('Visão Geral de Inscrições')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              {authContext === 'parish'
                ? 'Acompanhamento dos eventos em tempo real.'
                : authContext === 'admin'
                ? 'Visão geral administrativa de eventos e paróquias.'
                : t('Acompanhe suas inscrições e próximos passos.')}
            </Typography>
          </Box>
          <Chip
            size="small"
            color="success"
            label={authContext === 'admin' ? 'ADMINISTRADOR' : 'SISTEMA ATIVO'}
            sx={{ fontWeight: 700, letterSpacing: 0.4 }}
          />
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          display: { xs: 'block', md: 'none' },
          p: 2,
          mb: 2,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" spacing={1.2} alignItems="center">
            <Avatar alt={user?.name} />
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                {user?.name || 'Event Curator'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Painel operacional
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" color="inherit">
            <NotificationsNoneIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        authContext === 'admin' ? (
          <Paper sx={{ p: 4, borderRadius: 3, textAlign: 'center' }}>
            <Typography variant="h5" gutterBottom color="primary" sx={{ fontWeight: 700 }}>
              Painel de Super Administrador
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Use o menu lateral para monitorar operações, gerir paróquias e acompanhar indicadores gerais.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button variant="contained" color="primary" href="/events" sx={{ minWidth: 180 }}>
                Gerenciar Eventos
              </Button>
              <Button variant="outlined" color="primary" href="/parishes" sx={{ minWidth: 180 }}>
                Gerenciar Paróquias
              </Button>
            </Stack>
          </Paper>
        ) : authContext === 'parish' ? (
          <>
            <Grid container spacing={2.5} sx={{ mb: 4 }}>
              {statsCards.map((card) => (
                <Grid item xs={card.title === 'Total de Eventos' || card.title === 'Receita Total' ? 12 : 6} sm={6} md={3} key={card.title}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2.5,
                      height: '100%',
                      borderRadius: 3,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderLeft: '4px solid',
                      borderLeftColor: card.color,
                    }}
                  >
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.5 }}>
                        {card.title.toUpperCase()}
                      </Typography>
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.12),
                          color: 'primary.main',
                        }}
                      >
                        {card.icon}
                      </Avatar>
                    </Stack>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        mb: 0.5,
                        fontSize: { xs: card.title === 'Receita Total' ? '2rem' : '2.125rem', md: '2.125rem' },
                        lineHeight: 1.1,
                        wordBreak: 'break-word',
                      }}
                    >
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {card.helper}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mb: 4 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700 }}>
                  Eventos em Destaque
                </Typography>
                <Button variant="text" href="/dashboard/eventos">
                  Ver todos
                </Button>
              </Stack>
              <Grid container spacing={2.5}>
                {highlightedEvents.map((event) => {
                  const statusMeta = formatEventStatus(event.status);
                  return (
                    <Grid item xs={12} md={4} key={event.id}>
                      <Paper
                        sx={{
                          borderRadius: 3,
                          overflow: 'hidden',
                          border: '1px solid',
                          borderColor: 'divider',
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          cursor: 'pointer',
                        }}
                        onClick={() => navigate(`/dashboard/eventos/${event.id}/workspace`)}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            height: 180,
                            backgroundColor: 'grey.200',
                            backgroundImage: event.coverImage ? `url(${event.coverImage})` : undefined,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }}
                        >
                          <Chip
                            label={statusMeta.label}
                            color={statusMeta.color}
                            size="small"
                            sx={{
                              position: 'absolute',
                              top: 12,
                              right: 12,
                              fontWeight: 700,
                            }}
                          />
                        </Box>
                        <Box sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>
                          <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.8 }}>
                            {event.title}
                          </Typography>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 1 }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" noWrap title={event.locationLabel}>
                              {event.locationLabel}
                            </Typography>
                          </Stack>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Início: {event.startDate.toLocaleDateString('pt-BR')}
                          </Typography>
                          <Box sx={{ mt: 'auto' }}>
                            <Button
                              fullWidth
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/dashboard/eventos/${event.id}/workspace`);
                              }}
                              endIcon={<ArrowForwardIcon />}
                            >
                              Ver detalhes
                            </Button>
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
                {!highlightedEvents.length && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: 3, border: '1px dashed', borderColor: 'divider', textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum evento disponível para destaque no momento.
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 1.5 }}>
                {t('Inscrições Recentes')}
              </Typography>
              <Paper sx={{ p: 1.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Stack spacing={1}>
                  {recentRegistrations.slice(0, 4).map((reg) => (
                    <Box
                      key={reg.id}
                      sx={{
                        p: 1.2,
                        borderRadius: 2,
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                        <Avatar sx={{ width: 30, height: 30, fontSize: 12, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                          {(reg.participantName || 'U').slice(0, 2).toUpperCase()}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>
                            {reg.participantName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap>
                            {reg.eventTitle}
                          </Typography>
                        </Box>
                      </Stack>
                      <Chip
                        size="small"
                        label={reg.status === 'confirmed' ? 'Aprovado' : 'Pendente'}
                        color={reg.status === 'confirmed' ? 'success' : 'warning'}
                        sx={{ fontWeight: 700 }}
                      />
                    </Box>
                  ))}
                  {!recentRegistrations.length && (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                      {t('Nenhuma inscrição recente.')}
                    </Typography>
                  )}
                </Stack>
              </Paper>
            </Box>

            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
                Próximos Passos
              </Typography>
              <Paper sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
                <Grid container spacing={2}>
                  {AUDIENCE_FUNNEL_UI_ENABLED &&
                    user?.parish?.features?.audienceFunnelBetaEnabled !== true && (
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) }}>
                        <Typography variant="h6" gutterBottom>
                          Novidades em beta
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Seja o primeiro a testar Público, Filtros e Funil.
                        </Typography>
                        <Button variant="contained" color="primary" onClick={() => navigate('/dashboard/novidades')}>
                          Ver Novidades
                        </Button>
                      </Box>
                    </Grid>
                  )}
                  {user?.parish?.features?.customDomainPagesEnabled && (
                    <Grid item xs={12} md={4}>
                      <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) }}>
                        <Typography variant="h6" gutterBottom>
                          Configure sua página pública
                        </Typography>
                        <Typography variant="body2" color="text.secondary" paragraph>
                          Landing com seus eventos e domínio personalizado.
                        </Typography>
                        <Button variant="contained" color="primary" onClick={() => navigate('/dashboard/minha-pagina')}>
                          Minha página
                        </Button>
                      </Box>
                    </Grid>
                  )}
                  {canCreateEvents && (
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) }}>
                      <Typography variant="h6" gutterBottom>
                        Crie seu primeiro evento
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Configure detalhes, ingressos e formulários personalizados.
                      </Typography>
                      <Button variant="contained" color="primary" href="/dashboard/eventos/novo">
                        Criar Evento
                      </Button>
                    </Box>
                  </Grid>
                  )}
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) }}>
                      <Typography variant="h6" gutterBottom>
                        Complete seu perfil
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        Adicione logo, informações de contato e {t('descrição da paróquia.')}
                      </Typography>
                      <Button variant="outlined" color="primary" href="/dashboard/perfil">
                        Editar Perfil
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04) }}>
                      <Typography variant="h6" gutterBottom>
                        Explore a plataforma
                      </Typography>
                      <Typography variant="body2" color="text.secondary" paragraph>
                        {t('Conheça todos os recursos disponíveis para sua paróquia.')}
                      </Typography>
                      <Button variant="outlined" color="primary" href="/dashboard/ajuda">
                        Ver Tutorial
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>
            </Box>

            <Paper sx={{ p: 2.5, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Saúde da operação
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Indicadores rápidos para acompanhamento geral.
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between">
                <Typography variant="body2">
                  Taxa de ativação de eventos: <strong>{safeStats.totalEvents > 0 ? Math.round((safeStats.activeEvents / safeStats.totalEvents) * 100) : 0}%</strong>
                </Typography>
                <Typography variant="body2">
                  {t('Média de inscrições por evento:')} <strong>{safeStats.totalEvents > 0 ? (safeStats.totalRegistrations / safeStats.totalEvents).toFixed(1) : '0.0'}</strong>
                </Typography>
                <Typography variant="body2">
                  {t('Receita média por inscrição:')} <strong>{safeStats.totalRegistrations > 0 ? formatCurrency(safeStats.totalRevenue / safeStats.totalRegistrations) : formatCurrency(0)}</strong>
                </Typography>
              </Stack>
            </Paper>

          </>
        ) : (
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="h6" color="text.secondary">
              Usuário não reconhecido ou sem permissões.
            </Typography>
          </Box>
        )
      )}

      {user?.userType === 'participant' && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }} gutterBottom>
            {t('Suas Inscrições')}
          </Typography>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="body1" color="text.secondary" align="center">
              {t('Você ainda não tem inscrições em eventos.')}
            </Typography>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button variant="contained" color="primary" href="/" endIcon={<ArrowForwardIcon />}>
                Explorar Eventos
              </Button>
            </Box>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default DashboardPage;
